import React, { useState, useEffect, useRef } from 'react';
import NavigationBar from './NavigationBar';
import HamburgerMenu from './HamburgerMenu';
import Footer from './Footer';
import { 
  getLeaderboard, 
  getAllTimeBestSolvers, 
  getUserRankOnLeaderboard,
  getCurrentDate,
  getYesterdayDate
} from './supabaseClient';

function LeaderboardPage({ navigateTo, goBack, isLoggedIn, userProfile }) {
  const [selectedDifficulty, setSelectedDifficulty] = useState('hard');
  const [selectedTimeframe, setSelectedTimeframe] = useState('today');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifySettings, setNotifySettings] = useState({
    tomorrowPuzzle: true,
    leaderboardLocked: false,
    dailyReminder: false
  });
  
  // Real data state
  const [leaderboardData, setLeaderboardData] = useState({
    today: { easy: [], hard: [], master: [] },
    yesterday: { easy: [], hard: [], master: [] },
    alltime: { easy: [], hard: [], master: [] }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [userRank, setUserRank] = useState(null);
  
  const userRowRef = useRef(null);
  const leaderboardTopRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch leaderboard data when difficulty or timeframe changes
  useEffect(() => {
    fetchLeaderboardData();
  }, [selectedDifficulty, selectedTimeframe]);

  // Fetch user's rank when logged in and data is loaded
  useEffect(() => {
    if (isLoggedIn && userProfile && selectedTimeframe === 'today') {
      fetchUserRank();
    }
  }, [isLoggedIn, userProfile, selectedDifficulty, selectedTimeframe, leaderboardData]);

  const fetchLeaderboardData = async () => {
    setIsLoading(true);
    
    try {
      const today = getCurrentDate();
      const yesterday = getYesterdayDate();

      if (selectedTimeframe === 'today') {
        const data = await getLeaderboard(selectedDifficulty, today);
        setLeaderboardData(prev => ({
          ...prev,
          today: { ...prev.today, [selectedDifficulty]: data }
        }));
      } else if (selectedTimeframe === 'yesterday') {
        const data = await getLeaderboard(selectedDifficulty, yesterday);
        setLeaderboardData(prev => ({
          ...prev,
          yesterday: { ...prev.yesterday, [selectedDifficulty]: data }
        }));
      } else if (selectedTimeframe === 'alltime') {
        const data = await getAllTimeBestSolvers(selectedDifficulty, 50);
        setLeaderboardData(prev => ({
          ...prev,
          alltime: { ...prev.alltime, [selectedDifficulty]: data }
        }));
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserRank = async () => {
    if (!userProfile?.id) return;
    
    try {
      const today = getCurrentDate();
      const rank = await getUserRankOnLeaderboard(userProfile.id, selectedDifficulty, today);
      setUserRank(rank);
    } catch (error) {
      console.error('Error fetching user rank:', error);
    }
  };

  const currentLeaderboard = leaderboardData[selectedTimeframe][selectedDifficulty];
  const displayLimit = isLoggedIn ? currentLeaderboard.length : 10;
  
  // Check if user has completed this difficulty today
  const userHasCompletedThisDifficulty = userRank !== null;
  const userIsEligible = userRank?.rank !== undefined;

  useEffect(() => {
    if (selectedTimeframe === 'today' && isLoggedIn && userRowRef.current && userHasCompletedThisDifficulty) {
      setTimeout(() => {
        userRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } else {
      leaderboardTopRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedDifficulty, selectedTimeframe]);

  const handleDifficultyChange = (difficulty) => {
    setSelectedDifficulty(difficulty);
  };

  const handleTimeframeChange = (timeframe) => {
    setSelectedTimeframe(timeframe);
  };

  const handleNotifySave = () => {
    alert(`Notifications saved!\nTomorrow's puzzle: ${notifySettings.tomorrowPuzzle}\nLeaderboard locked: ${notifySettings.leaderboardLocked}\nDaily reminder: ${notifySettings.dailyReminder}`);
    setShowNotifyModal(false);
  };

  const FounderBadge = ({ tier }) => {
    if (tier === 'none') return null;
    
    const isFilled = tier === 'paid';
    const color = tier === 'paid' ? '#B58B2E' : '#2563EB';
    
    return (
      <svg width="16" height="18" viewBox="0 0 16 18" style={{ marginLeft: '6px' }}>
        <path
          d="M8 0 L12 4 L16 4 L16 11 L8 18 L0 11 L0 4 L4 4 Z"
          fill={isFilled ? color : 'none'}
          stroke={color}
          strokeWidth="1.5"
        />
      </svg>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
      padding: isMobile ? '16px' : '24px 20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {!isMobile && <NavigationBar currentPage="leaderboard" isLoggedIn={isLoggedIn} navigateTo={navigateTo} userProfile={userProfile} />}
      {isMobile && <HamburgerMenu currentPage="leaderboard" isLoggedIn={isLoggedIn} navigateTo={navigateTo} userProfile={userProfile} />}
      
      {/* Notify Modal */}
      {showNotifyModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => setShowNotifyModal(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '450px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
          >
            <h2 style={{
              fontSize: '24px',
              fontWeight: '800',
              color: '#1F2937',
              margin: '0 0 8px 0'
            }}>
              Email Notifications
            </h2>
            <p style={{
              fontSize: '14px',
              color: '#6B7280',
              margin: '0 0 24px 0'
            }}>
              Notifications sent at midday UTC
            </p>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                marginBottom: '16px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={notifySettings.tomorrowPuzzle}
                  onChange={(e) => setNotifySettings({ ...notifySettings, tomorrowPuzzle: e.target.checked })}
                  style={{
                    width: '20px',
                    height: '20px',
                    marginTop: '2px',
                    cursor: 'pointer'
                  }}
                />
                <div>
                  <div style={{ fontSize: '15px', fontWeight: '600', color: '#1F2937' }}>
                    Tomorrow's puzzle is live
                  </div>
                  <div style={{ fontSize: '13px', color: '#6B7280' }}>
                    Get notified when the next daily puzzle is available
                  </div>
                </div>
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                marginBottom: '16px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={notifySettings.leaderboardLocked}
                  onChange={(e) => setNotifySettings({ ...notifySettings, leaderboardLocked: e.target.checked })}
                  style={{
                    width: '20px',
                    height: '20px',
                    marginTop: '2px',
                    cursor: 'pointer'
                  }}
                />
                <div>
                  <div style={{ fontSize: '15px', fontWeight: '600', color: '#1F2937' }}>
                    Today's leaderboard results are locked in
                  </div>
                  <div style={{ fontSize: '13px', color: '#6B7280' }}>
                    Get notified when final rankings are set
                  </div>
                </div>
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={notifySettings.dailyReminder}
                  onChange={(e) => setNotifySettings({ ...notifySettings, dailyReminder: e.target.checked })}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer'
                  }}
                />
                <div style={{ fontSize: '15px', fontWeight: '600', color: '#1F2937' }}>
                  Make this a daily reminder
                </div>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowNotifyModal(false)}
                style={{
                  flex: '1',
                  padding: '12px',
                  background: '#F3F4F6',
                  color: '#4B5563',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleNotifySave}
                style={{
                  flex: '1',
                  padding: '12px',
                  background: '#FF8C00',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(255, 140, 0, 0.4)'
                }}
              >
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Single Column Layout */}
      <div style={{
        maxWidth: '900px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '20px',
          color: 'white'
        }}>
          <h1 style={{
            fontSize: isMobile ? '28px' : '36px',
            fontWeight: '800',
            margin: '0 0 8px 0'
          }}>
            Today's Leaderboard
          </h1>
          <p style={{
            fontSize: '14px',
            opacity: 0.8,
            margin: '0'
          }}>
            Daily rankings reset every 24 hours
          </p>
        </div>

        {/* Difficulty Tabs - Sticky on Mobile */}
        <div style={{
          position: isMobile ? 'sticky' : 'relative',
          top: isMobile ? '0' : 'auto',
          zIndex: 10,
          background: isMobile ? 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)' : 'transparent',
          padding: isMobile ? '12px 0' : '0',
          marginBottom: '16px'
        }}>
          <div style={{
            display: 'flex',
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '4px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            {['easy', 'hard', 'master'].map(difficulty => (
              <button
                key={difficulty}
                onClick={() => handleDifficultyChange(difficulty)}
                style={{
                  flex: '1',
                  padding: '10px',
                  background: selectedDifficulty === difficulty ? 'white' : 'transparent',
                  color: selectedDifficulty === difficulty ? '#1F2937' : 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textTransform: 'capitalize'
                }}
              >
                {difficulty}
              </button>
            ))}
          </div>
        </div>

        {/* Warning: User hasn't completed this difficulty */}
        {isLoggedIn && selectedTimeframe === 'today' && !userHasCompletedThisDifficulty && (
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '12px',
            padding: '14px 18px',
            marginBottom: '16px',
            color: 'white',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            Solve today's {selectedDifficulty.charAt(0).toUpperCase() + selectedDifficulty.slice(1)} puzzle to appear here.
          </div>
        )}

        {/* Warning: User completed but not eligible */}
        {isLoggedIn && selectedTimeframe === 'today' && userHasCompletedThisDifficulty && !userIsEligible && (
          <div style={{
            background: 'rgba(251, 191, 36, 0.15)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(251, 191, 36, 0.3)',
            borderRadius: '12px',
            padding: '14px 18px',
            marginBottom: '16px',
            color: '#FCD34D',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            This solve isn't eligible for today's leaderboard.
          </div>
        )}

        {/* Leaderboard Card */}
        <div style={{
          display: 'flex',
          gap: '20px',
          alignItems: 'flex-start'
        }}>
          <div style={{
            flex: '1',
            maxWidth: isMobile ? '92%' : '100%',
            margin: isMobile ? '0 auto' : '0'
          }}>
            {/* Timeframe Tabs - Integrated into Card Top */}
            <div style={{
              display: 'flex',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '12px 12px 0 0',
              padding: '4px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderBottom: 'none'
            }}>
              {[
                { key: 'today', label: 'Today' },
                { key: 'yesterday', label: 'Yesterday' },
                { key: 'alltime', label: 'All-Time' }
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => handleTimeframeChange(key)}
                  style={{
                    flex: '1',
                    padding: '8px',
                    background: selectedTimeframe === key ? 'white' : 'transparent',
                    color: selectedTimeframe === key ? '#1F2937' : 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Leaderboard Table */}
            <div style={{
              background: 'white',
              borderRadius: '0 0 16px 16px',
              overflow: 'hidden',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
            }}>
              {/* Table Header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '50px 1fr 80px' : '60px 1fr 100px 80px',
                padding: '16px 20px',
                background: '#F9FAFB',
                borderBottom: '1px solid #E5E7EB',
                fontSize: '12px',
                fontWeight: '700',
                color: '#6B7280',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                <div>Rank</div>
                <div>Player</div>
                {!isMobile && <div style={{ textAlign: 'center' }}>Mistakes</div>}
                <div style={{ textAlign: 'right' }}>Time</div>
              </div>

              {/* Table Body */}
              <div
                ref={leaderboardTopRef}
                style={{
                  maxHeight: isMobile ? '55vh' : '500px',
                  overflowY: 'auto'
                }}
              >
                {isLoading ? (
                  <div style={{
                    padding: '48px 24px',
                    textAlign: 'center',
                    color: '#6B7280',
                    fontSize: '15px'
                  }}>
                    Loading leaderboard...
                  </div>
                ) : currentLeaderboard.length === 0 ? (
                  <div style={{
                    padding: '48px 24px',
                    textAlign: 'center',
                    color: '#6B7280',
                    fontSize: '15px'
                  }}>
                    No solvers yet. Be the first!
                  </div>
                ) : (
                  <>
                    {currentLeaderboard.slice(0, displayLimit).map((entry) => {
                      const isCurrentUser = isLoggedIn && userProfile && entry.userId === userProfile.id;
                      
                      return (
                        <div
                          key={`${entry.userId}-${entry.rank}`}
                          ref={isCurrentUser ? userRowRef : null}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '50px 1fr 80px' : '60px 1fr 100px 80px',
                            padding: '14px 20px',
                            background: entry.rank <= 3 
                              ? 'rgba(255, 140, 0, 0.05)' 
                              : isCurrentUser 
                                ? 'rgba(37, 99, 235, 0.08)' 
                                : 'white',
                            borderBottom: '1px solid #F3F4F6',
                            borderLeft: isCurrentUser ? '3px solid #2563EB' : '3px solid transparent',
                            alignItems: 'center'
                          }}
                        >
                          <div style={{
                            fontSize: '16px',
                            fontWeight: '700',
                            color: entry.rank <= 3 ? '#FF8C00' : '#1F2937'
                          }}>
                            #{entry.rank}
                          </div>

                          <div style={{
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            <span style={{
                              fontSize: '15px',
                              fontWeight: '600',
                              color: '#1F2937'
                            }}>
                              {entry.username}
                              {isCurrentUser && (
                                <span style={{
                                  marginLeft: '6px',
                                  fontSize: '12px',
                                  color: '#2563EB',
                                  fontWeight: '500'
                                }}>
                                  (You)
                                </span>
                              )}
                            </span>
                            <FounderBadge tier={entry.founderTier} />
                          </div>

                          {!isMobile && (
                            <div style={{
                              textAlign: 'center',
                              fontSize: '14px',
                              color: '#6B7280'
                            }}>
                              {entry.mistakes !== undefined ? entry.mistakes : '—'}
                            </div>
                          )}

                          <div style={{
                            textAlign: 'right',
                            fontSize: '16px',
                            fontWeight: '700',
                            fontFamily: 'monospace',
                            color: '#1F2937'
                          }}>
                            {entry.time}
                          </div>
                        </div>
                      );
                    })}

                    {/* Guest User Blur/CTA */}
                    {!isLoggedIn && currentLeaderboard.length > 10 && (
                      <div style={{ position: 'relative', minHeight: '200px' }}>
                        <div style={{
                          filter: 'blur(4px)',
                          opacity: 0.4,
                          pointerEvents: 'none'
                        }}>
                          {currentLeaderboard.slice(10, 15).map((entry) => (
                            <div key={entry.rank} style={{
                              display: 'grid',
                              gridTemplateColumns: isMobile ? '50px 1fr 80px' : '60px 1fr 100px 80px',
                              padding: '14px 20px',
                              borderBottom: '1px solid #F3F4F6'
                            }}>
                              <div>#{entry.rank}</div>
                              <div>{entry.username}</div>
                              {!isMobile && <div style={{ textAlign: 'center' }}>{entry.mistakes || '—'}</div>}
                              <div style={{ textAlign: 'right', fontFamily: 'monospace' }}>{entry.time}</div>
                            </div>
                          ))}
                        </div>

                        <div style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          padding: '32px 24px',
                          background: 'linear-gradient(to top, rgba(255,255,255,0.98), rgba(255,255,255,0.85))',
                          backdropFilter: 'blur(8px)',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔒</div>
                          <div style={{
                            fontSize: '15px',
                            color: '#4B5563',
                            marginBottom: '16px',
                            fontWeight: '500'
                          }}>
                            Create an account to see the full leaderboard and your position.
                          </div>
                          <button
                            onClick={() => navigateTo('login')}
                            style={{
                              padding: '12px 32px',
                              background: '#FF8C00',
                              color: 'white',
                              border: 'none',
                              borderRadius: '10px',
                              fontSize: '15px',
                              fontWeight: '700',
                              cursor: 'pointer',
                              boxShadow: '0 4px 12px rgba(255, 140, 0, 0.4)'
                            }}
                          >
                            Create Account
                          </button>
                        </div>
                      </div>
                    )}

                    {currentLeaderboard.length < 5 && (
                      <div style={{
                        padding: '24px',
                        textAlign: 'center',
                        color: '#6B7280',
                        fontSize: '14px'
                      }}>
                        You're early today — more solvers will appear soon.
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Action Buttons */}
        <div style={{
          marginTop: '24px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: '12px',
          maxWidth: isMobile ? '92%' : '100%',
          margin: '24px auto 0'
        }}>
          <button
            onClick={() => navigateTo('difficulty')}
            style={{
              flex: '1',
              padding: '14px 24px',
              background: '#FF8C00',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(255, 140, 0, 0.4)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#E07B00';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#FF8C00';
            }}
          >
            Solve Another Difficulty
          </button>
          <button
            onClick={goBack}
            style={{
              flex: '1',
              padding: '14px 24px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            Back to Puzzle
          </button>
          <button
            onClick={() => setShowNotifyModal(true)}
            style={{
              flex: '1',
              padding: '14px 24px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            Notify Me
          </button>
        </div>
      </div>

      <div style={{ marginTop: '40px' }}>
        <Footer navigateTo={navigateTo} />
      </div>
    </div>
  );
}

export default LeaderboardPage;
